
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface Service {
  title: string;
  icon: string;
  description: string;
  link: string;
}

const services: Service[] = [
  {
    title: "Web/App Development",
    icon: "💻",
    description: "Custom website design and development, mobile apps, e-commerce solutions, and web applications.",
    link: "/services/web-development"
  },
  {
    title: "Digital Marketing",
    icon: "📊",
    description: "SEO, SEM, social media marketing, content strategy, and analytics to grow your online presence.",
    link: "/services/digital-marketing"
  },
  {
    title: "Video Ads Creation",
    icon: "🎬",
    description: "Engaging video advertisements, product demos, explainer videos, and commercial content.",
    link: "/services/video-ads"
  },
  {
    title: "2D Animation",
    icon: "🎨",
    description: "Character animation, motion graphics, animated explainer videos, and interactive content.",
    link: "/services/animation"
  },
  {
    title: "Graphics/Logo Design",
    icon: "✏️",
    description: "Brand identity, logo design, marketing materials, UI/UX design, and visual assets.",
    link: "/services/design"
  },
  {
    title: "Video Editing",
    icon: "🎥",
    description: "Professional video editing, color correction, special effects, transitions, and post-production.",
    link: "/services/video-editing"
  },
  {
    title: "Influencer Marketing",
    icon: "🌟",
    description: "Influencer partnerships, campaign management, content creation, and performance tracking.",
    link: "/services/influencer-marketing"
  },
  {
    title: "Social Media Management",
    icon: "📱",
    description: "Content creation, community management, scheduling, analytics, and strategy development.",
    link: "/services/social-media"
  },
];

const ServicesSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section className="py-20 px-6" ref={sectionRef} id="services">
      <div className="container mx-auto">
        <h2 className="section-heading">
          Our <span className="text-blue-500">Services</span>
        </h2>
        <p className="section-subheading">
          We provide end-to-end digital solutions to help your business grow and thrive in the digital landscape.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className={`group glass-card rounded-xl p-6 transition-all duration-700 transform perspective hover:scale-105 hover:shadow-xl ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-16'
              }`}
              style={{ 
                transitionDelay: `${index * 100}ms`,
                transformStyle: 'preserve-3d'
              }}
            >
              <div 
                className="text-4xl mb-4 bg-gradient-to-br from-blue-500 to-orange-500 text-white w-16 h-16 rounded-lg flex items-center justify-center shadow-lg transform group-hover:rotate-y-12 transition-transform duration-300"
              >
                <span>{service.icon}</span>
              </div>
              <h3 className="text-xl font-bold mb-2">{service.title}</h3>
              <p className="text-gray-600 mb-4">{service.description}</p>
              <Link
                to={service.link}
                className="inline-flex items-center text-blue-500 font-medium group-hover:text-blue-700 transition-colors relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-blue-500 after:transition-all after:duration-300 group-hover:after:w-full"
                aria-label={`Learn more about our ${service.title} service`}
              >
                Learn More
                <svg
                  className="w-5 h-5 ml-1 transform transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  ></path>
                </svg>
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link
            to="/services"
            className="inline-block px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
            aria-label="Explore all our professional services"
          >
            Explore All Services
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
